// The account menu's surface and rows (Figma 952:30970), shared by every menu built to match it.

/**
 * The menu surface, from Figma 952:30970 (accounts) and 952:30961 (brands).
 *
 * Both panels are the same component in the design, so the classes live here once rather
 * than being retyped per panel and drifting apart. Raw hex for the same reason the rest of
 * this file uses it: these are design-system values with no Tailwind token behind them yet.
 */
export const PANEL =
	'flex flex-col gap-[6px] rounded-[16px] border border-gray-300 bg-white/80 p-[6px] shadow-[0px_3px_12px_0px_rgba(0,0,0,0.08)] backdrop-blur-[6px]';

/** The 11px section heading above each list — "Account", "My Brands". */
export const SECTION_LABEL = 'px-[6px] pt-[4px] text-[11px] font-medium capitalize leading-[1.1] text-gray-700';

/**
 * One row. The 1px padding is the design's: it is what leaves the 10px radius visible as a
 * ring when the row is filled, so a selected row reads as a chip rather than a full-bleed bar.
 */
export const ROW =
	'flex w-full min-w-[48px] items-center rounded-[10px] p-[1px] text-[13px] leading-[18px] text-ink outline-hidden';

/** The inner box, which carries the real padding. Split from ROW so the 1px ring stays clean. */
export const ROW_INNER = 'flex flex-1 items-center gap-[8px] py-[4px] pr-[6px] pl-[10px]';

/**
 * Two state layers, not one.
 *
 * SELECTED is the heavier of the two and is permanent: whichever account you are in, and
 * whichever brand you are scoped to, reads as chosen whether or not the pointer is near it.
 * HOVER is half that, so passing over a row never looks like selecting it — which is what
 * one shared layer did, leaving no way to tell the current account from the one under the
 * cursor.
 *
 * `focus` rather than `hover` because Radix moves focus to the item the pointer is over,
 * so the keyboard and the mouse land on the same style instead of two that disagree.
 */
//
// ONLY THE BACKGROUND MOVES. The `DropdownMenuItem` and `SubTrigger` wrappers both set
// `focus:text-muted-foreground`, which greys the label the moment the pointer arrives —
// the row appeared to fade as you reached for it. Pinned back to the row's own colour on
// both states so hovering changes the fill and nothing else.
export const ROW_HOVER =
	'focus:bg-ink/4 focus:text-ink data-[state=open]:bg-ink/4 data-[state=open]:text-ink';
export const ROW_SELECTED = 'bg-ink/8';

// Row "⋯" menus in settings tables (Edit / Disable / Delete): the same surface, 13px items, red for destructive.
export const ROW_MENU_CONTENT = `${PANEL} min-w-[160px]`;
export const ROW_MENU_ITEM = 'cursor-pointer gap-2 rounded-[10px] px-2.5 py-1 text-[13px] text-ink focus:bg-gray-100 [&_svg]:size-3.5';
export const ROW_MENU_ITEM_DANGER = 'text-red-500 focus:text-red-500 [&_svg]:text-red-500';
